import React from 'react';
import { Alert, Button, Card, CardBody, Col, Form, Row, Spinner, Tab, Tabs } from '@edgebox/react-components';
import { JsonView, collapseAllNested } from 'react-json-view-lite';

import { ISyncCoreApiComponentState, RequestReason, SyncCoreApiComponent } from '../../../../services/SyncCoreApiComponent';
import type {
  EvaluateFormulaResponse,
  FormulaConfigResponse,
  FormulaErrorDto,
  ValidateFormulaResponse,
} from '@edgebox/sync-core-rest-client/dist/services/api';

import { jsonStyle } from '../../../../Helpers';
import { FunctionHelp } from './FunctionHelp';
import { FormulaRegistryTab } from './FormulaRegistryTab';
import { FormulaResultTab } from './FormulaResultTab';
import { resolveFunctionHelp } from './formulaFunctionHelp';

type GenerateFormulaWithAiRequest = {
  requirement: string;
  variableTypes: Record<string, unknown>;
  allowedCategories: null;
  allowedOutputTypes: null;
};

type GenerateFormulaWithAiResponse = ValidateFormulaResponse & {
  formula: string;
};

interface IState extends ISyncCoreApiComponentState {
  config?: FormulaConfigResponse;
  formula: string;
  variablesJson: string;
  variableTypesJson: string;
  validateResult?: ValidateFormulaResponse;
  evaluateResult?: EvaluateFormulaResponse;
  variablesJsonError?: string;
  variableTypesJsonError?: string;
  selectedFunctionName?: string;
  selectedFunctionDescription?: string;
  aiRequirement: string;
  aiRequirementError?: string;
  aiLastRequest?: GenerateFormulaWithAiRequest;
  aiGenerateResult?: GenerateFormulaWithAiResponse;
}

const DEFAULT_FORMULA = `IF(
  value.active,
  AVG(
    EACH(
      LIST(
        value.count,
        value.count + 1,
        LEN(value.title),
        100
      ),
      "+",
      10
    )
  ),
  0
)`;

const DEFAULT_VARIABLES = `{
  "value": {
    "active": true,
    "count": 41,
    "title": "Hello from the formula playground"
  }
}`;

const DEFAULT_VARIABLE_TYPES = `{
  "value": {
    "description": "The item you are evaluating in this example.",
    "kind": "object",
    "clarifier": "Item",
    "nullable": true,
    "properties": {
      "active": { "description": "Whether the item is active.", "kind": "boolean" },
      "count": { "description": "A sample number used in this example.", "kind": "number" },
      "title": { "description": "A sample text value used in this example.", "kind": "text" }
    }
  }
}`;

function normalizeBaseUrl(baseUrl: string): string {
  if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) return baseUrl;
  if (baseUrl.startsWith('//')) return `${window.location.protocol}${baseUrl}`;
  return `${window.location.protocol}//${baseUrl}`;
}

function normalizeStaticField<T extends Record<string, any>>(value: unknown): T {
  if (!value || typeof value !== 'object') return value as T;
  const obj = value as Record<string, any>;
  if (obj._static === undefined && obj.static !== undefined) obj._static = obj.static;
  return obj as T;
}

export class FormulaPlayground extends SyncCoreApiComponent<{}, IState> {
  private validateTimer: ReturnType<typeof setTimeout> | undefined;
  private textareaRef = React.createRef<HTMLTextAreaElement>();

  state: IState = {
    apiErrors: {},
    openRequests: {},
    formula: DEFAULT_FORMULA,
    variablesJson: DEFAULT_VARIABLES,
    variableTypesJson: DEFAULT_VARIABLE_TYPES,
    aiRequirement: '',
  };

  async load(): Promise<Partial<IState>> {
    const config = await this.api.utility.formula.config();
    return { config };
  }

  componentDidUpdate(_prevProps: {}, prevState: IState): void {
    // After config is loaded (first time), initialize the function help with the top-most function.
    if (!prevState.config && this.state.config && !this.state.selectedFunctionName) {
      this.updateSelectedFunction(this.state.formula, 0);
    }
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();
    if (this.validateTimer) clearTimeout(this.validateTimer);
  }

  private updateSelectedFunction(nextFormula?: string, selectionStart?: number | null) {
    const config = this.state.config;
    const formula = nextFormula ?? this.state.formula;
    const cursor = typeof selectionStart === 'number' ? selectionStart : (this.textareaRef.current?.selectionStart ?? 0);

    const parsedVariableTypes = this.parseVariableTypesJson(this.state.variableTypesJson);
    const variableTypesOverride = parsedVariableTypes.ok ? parsedVariableTypes.value : undefined;

    const { name, description } = resolveFunctionHelp(config, formula, cursor, variableTypesOverride);
    if (!name) {
      this.setState({ selectedFunctionName: undefined, selectedFunctionDescription: undefined });
      return;
    }

    this.setState({ selectedFunctionName: name, selectedFunctionDescription: description });
  }

  private scheduleValidate(nextFormula: string) {
    if (this.validateTimer) clearTimeout(this.validateTimer);
    this.validateTimer = setTimeout(() => {
      this.validate(nextFormula);
    }, 300);
  }

  private async validate(formula: string) {
    const variableTypes = this.parseVariableTypesJson(this.state.variableTypesJson);
    if (!variableTypes.ok) {
      this.setState({ variableTypesJsonError: variableTypes.error });
      return;
    }

    this.setState({ variableTypesJsonError: undefined });

    const res = await this.wrapApiCall(
      this.postFormula<ValidateFormulaResponse>('validate', {
        formula,
        allowedCategories: null,
        allowedOutputTypes: null,
        variableTypes: variableTypes.value,
      }),
      RequestReason.RequestDetails
    );

    if (res && this.__isMounted) {
      // Deep clone value to remove undefined values.
      const normalized = normalizeStaticField<ValidateFormulaResponse>(res);
      this.setState({ validateResult: JSON.parse(JSON.stringify(normalized)), evaluateResult: undefined });
    }
  }

  private parseVariablesJson(raw: string): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
    if (!raw.trim()) return { ok: true, value: {} };
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ok: false, error: 'Variables must be a JSON object.' };
      }
      return { ok: true, value: parsed as Record<string, unknown> };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }

  private parseVariableTypesJson(raw: string): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
    if (!raw.trim()) return { ok: true, value: {} };
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ok: false, error: 'Variable types must be a JSON object.' };
      }
      return { ok: true, value: parsed as Record<string, unknown> };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }

  private async postFormula<T>(endpoint: 'validate' | 'evaluate' | 'parse' | 'generate', body: Record<string, unknown>): Promise<T> {
    const baseUrl = this.context?.baseUrl;
    if (!baseUrl) {
      throw new Error('Missing Sync Core baseUrl in iframe config.');
    }

    const url = new URL(`/sync-core/formula/${endpoint}`, normalizeBaseUrl(baseUrl)).toString();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.context.jwt}`,
      },
      body: JSON.stringify(body),
    });

    const text = await res.text();
    const parsed = text
      ? (() => {
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        })()
      : undefined;

    if (!res.ok) {
      const msg = typeof parsed === 'object' && parsed && 'message' in (parsed as any) ? String((parsed as any).message) : String(text);
      throw new Error(msg || `Request failed (${res.status})`);
    }

    return parsed as T;
  }

  private async evaluate() {
    const { formula, variablesJson, variableTypesJson } = this.state;
    const vars = this.parseVariablesJson(variablesJson);
    if (!vars.ok) {
      this.setState({ variablesJsonError: vars.error });
      return;
    }

    const variableTypes = this.parseVariableTypesJson(variableTypesJson);
    if (!variableTypes.ok) {
      this.setState({ variableTypesJsonError: variableTypes.error });
      return;
    }

    this.setState({ variablesJsonError: undefined, variableTypesJsonError: undefined });

    const res = await this.wrapApiCall(
      this.postFormula<EvaluateFormulaResponse>('evaluate', {
        formula,
        variables: vars.value,
        allowedCategories: null,
        allowedOutputTypes: null,
        variableTypes: variableTypes.value,
        cacheKey: null,
      }),
      RequestReason.ExecuteAction
    );

    if (res && this.__isMounted) {
      // Deep clone value to remove undefined values.
      const normalized = normalizeStaticField<EvaluateFormulaResponse>(res);
      this.setState({ evaluateResult: JSON.parse(JSON.stringify(normalized)) });
    }
  }

  private async generateFormulaWithAi() {
    const requirement = this.state.aiRequirement.trim();
    if (!requirement) {
      this.setState({ aiRequirementError: 'Please provide instructions for the AI.' });
      return;
    }

    const variableTypes = this.parseVariableTypesJson(this.state.variableTypesJson);
    if (!variableTypes.ok) {
      this.setState({ variableTypesJsonError: variableTypes.error });
      return;
    }

    this.setState({ aiRequirementError: undefined, variableTypesJsonError: undefined });

    const body: GenerateFormulaWithAiRequest = {
      requirement,
      variableTypes: variableTypes.value,
      allowedCategories: null,
      allowedOutputTypes: null,
    };

    this.setState({ aiLastRequest: body });

    const res = await this.wrapApiCall(this.postFormula<GenerateFormulaWithAiResponse>('generate', body), RequestReason.GenerateAi);

    if (!res || !this.__isMounted) return;

    // Deep clone value to remove undefined values.
    const normalized = normalizeStaticField<GenerateFormulaWithAiResponse>(res);
    const cleaned = JSON.parse(JSON.stringify(normalized)) as GenerateFormulaWithAiResponse;

    this.setState({
      aiGenerateResult: cleaned,
      formula: cleaned.formula,
      validateResult: cleaned,
      evaluateResult: undefined,
    });

    this.updateSelectedFunction(cleaned.formula, 0);
  }

  private selectSpan(err: FormulaErrorDto) {
    if (!err.span || !this.textareaRef.current) return;
    const start = err.span.start ?? 0;
    const end = err.span.end ?? start;
    this.textareaRef.current.focus();
    this.textareaRef.current.setSelectionRange(start, end);
    this.updateSelectedFunction(this.state.formula, start);
  }

  render() {
    const {
      config,
      formula,
      variablesJson,
      variableTypesJson,
      validateResult,
      evaluateResult,
      variablesJsonError,
      variableTypesJsonError,
      selectedFunctionName,
      selectedFunctionDescription,
    } = this.state;

    if (!config) {
      return this.renderRequest(RequestReason.LoadComponent, 'throbber');
    }

    const validateError = this.state.apiErrors[RequestReason.RequestDetails];
    const evaluateError = this.state.apiErrors[RequestReason.ExecuteAction];
    const generateAiError = this.state.apiErrors[RequestReason.GenerateAi];

    const validating = !!this.state.openRequests[RequestReason.RequestDetails];
    const evaluating = !!this.state.openRequests[RequestReason.ExecuteAction];
    const generatingAi = !!this.state.openRequests[RequestReason.GenerateAi];

    return (
      <div className="p-2">
        <Card>
          <CardBody>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-bold">Formula playground</div>
                <div className="text-muted small">Validate and preview formulas against the current Sync Core registry.</div>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline" size="sm" onClick={() => this.validate(this.state.formula)} disabled={validating}>
                  {validating ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Validating
                    </>
                  ) : (
                    'Validate'
                  )}
                </Button>
                <Button variant="primary" size="sm" onClick={() => this.evaluate()} disabled={evaluating}>
                  {evaluating ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Evaluating
                    </>
                  ) : (
                    'Evaluate'
                  )}
                </Button>
              </div>
            </div>

            {validateError ? (
              <Alert variant="danger" className="mt-3 mb-0">
                {validateError.message}
              </Alert>
            ) : null}
            {evaluateError ? (
              <Alert variant="danger" className="mt-3 mb-0">
                {evaluateError.message}
              </Alert>
            ) : null}

            <Row className="mt-3 g-3">
              <Col md={7}>
                <Form.Group>
                  <Form.Label className="fw-bold">Formula</Form.Label>
                  <Form.Control
                    as="textarea"
                    ref={this.textareaRef}
                    value={formula}
                    rows={10}
                    spellCheck={false}
                    onChange={(e) => {
                      const next = e.target.value;
                      this.setState({ formula: next });
                      this.scheduleValidate(next);
                      this.updateSelectedFunction(next, e.target.selectionStart);
                    }}
                    onClick={(e) => this.updateSelectedFunction(undefined, (e.target as HTMLTextAreaElement).selectionStart)}
                    onKeyUp={(e) => this.updateSelectedFunction(undefined, (e.target as HTMLTextAreaElement).selectionStart)}
                    onSelect={(e) => this.updateSelectedFunction(undefined, (e.target as HTMLTextAreaElement).selectionStart)}
                  />
                </Form.Group>

                <FunctionHelp name={selectedFunctionName} description={selectedFunctionDescription} />

                <Form.Group className="mt-3">
                  <Form.Label className="fw-bold">Variables (JSON object)</Form.Label>
                  <Form.Control
                    as="textarea"
                    value={variablesJson}
                    rows={6}
                    spellCheck={false}
                    onChange={(e) => this.setState({ variablesJson: e.target.value, variablesJsonError: undefined })}
                    isInvalid={!!variablesJsonError}
                  />
                  {variablesJsonError ? <Form.Control.Feedback type="invalid">{variablesJsonError}</Form.Control.Feedback> : null}
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label className="fw-bold">Variable types override (JSON)</Form.Label>
                  <div className="text-muted small mb-1">
                    Defines which properties can be accessed (allowlist). Example: to allow <code>value.title</code>, add it under{' '}
                    <code>properties</code>.
                  </div>
                  <Form.Control
                    as="textarea"
                    value={variableTypesJson}
                    rows={7}
                    spellCheck={false}
                    onChange={(e) => this.setState({ variableTypesJson: e.target.value, variableTypesJsonError: undefined })}
                    isInvalid={!!variableTypesJsonError}
                  />
                  {variableTypesJsonError ? <Form.Control.Feedback type="invalid">{variableTypesJsonError}</Form.Control.Feedback> : null}
                </Form.Group>
              </Col>

              <Col md={5}>
                <Tabs defaultActiveKey="result" className="mb-2">
                  <Tab eventKey="result" title="Result">
                    <FormulaResultTab
                      validateResult={validateResult}
                      evaluateResult={evaluateResult}
                      onSelectSpan={(e) => this.selectSpan(e)}
                    />
                  </Tab>

                  <Tab eventKey="registry" title="Registry">
                    <FormulaRegistryTab config={config} />
                  </Tab>

                  <Tab eventKey="ai-generation" title="AI Generation">
                    <Card className="mt-2">
                      <CardBody>
                        <div className="fw-bold">Generate a formula from instructions</div>
                        <div className="text-muted small">
                          Provide a requirement in plain language. On success, the generated formula replaces the current formula in the
                          editor.
                        </div>

                        <Form.Group className="mt-3">
                          <Form.Label className="fw-bold">Instructions</Form.Label>
                          <Form.Control
                            as="textarea"
                            value={this.state.aiRequirement}
                            rows={5}
                            spellCheck={false}
                            placeholder="Example: If value.active is false return 0, otherwise average value.count, value.count+1 and the title length."
                            onChange={(e) => this.setState({ aiRequirement: e.target.value, aiRequirementError: undefined })}
                            isInvalid={!!this.state.aiRequirementError}
                          />
                          {this.state.aiRequirementError ? (
                            <Form.Control.Feedback type="invalid">{this.state.aiRequirementError}</Form.Control.Feedback>
                          ) : null}
                        </Form.Group>

                        <div className="d-flex align-items-center gap-2 mt-3">
                          <Button variant="primary" size="sm" onClick={() => this.generateFormulaWithAi()} disabled={generatingAi}>
                            {generatingAi ? (
                              <>
                                <Spinner size="sm" className="me-2" /> Generating
                              </>
                            ) : (
                              'Generate'
                            )}
                          </Button>
                          <div className="small text-muted">Uses the current “Variable types override” JSON.</div>
                        </div>

                        {generatingAi ? <div className="mt-2">{this.renderRequest(RequestReason.GenerateAi, 'bar')}</div> : null}
                        {generateAiError ? <div className="mt-2">{this.renderRequest(RequestReason.GenerateAi)}</div> : null}

                        {this.state.aiGenerateResult ? (
                          <Alert variant={this.state.aiGenerateResult.ok ? 'success' : 'warning'} className="mt-3 mb-0">
                            {this.state.aiGenerateResult.ok
                              ? 'Formula generated and applied to the editor.'
                              : 'Formula generated but did not validate cleanly. See errors in the Result tab.'}
                          </Alert>
                        ) : null}

                        {this.state.aiLastRequest ? (
                          <div className="mt-3">
                            <div className="fw-bold">Request payload</div>
                            <div className="mt-2">
                              <JsonView data={this.state.aiLastRequest} style={jsonStyle} shouldExpandNode={collapseAllNested} />
                            </div>
                          </div>
                        ) : null}

                        {this.state.aiGenerateResult ? (
                          <div className="mt-3">
                            <div className="fw-bold">Response</div>
                            <div className="mt-2">
                              <JsonView data={this.state.aiGenerateResult} style={jsonStyle} shouldExpandNode={collapseAllNested} />
                            </div>
                          </div>
                        ) : null}
                      </CardBody>
                    </Card>
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
    );
  }
}
